import { TransactionStatus } from '@cpce/database/enums';
import {
  ActionLogType,
  type BaseActionLog,
  type BaseEmployeeBeneficiary,
  type BaseEmployeeCommunication,
  computeEmployeeAlerts,
  deriveReglementDepositStatus,
  type Employee,
  EmployeeBeneficiaryStatus,
  type EmployeeDocument,
  EmployeeDocumentType,
  type EmployeeWithBeneficiaries,
  fullYearsSince,
  type PrestationTransactionWithEmployee,
  sumDecimals,
} from '@cpce/shared';

import type {
  EmployeeBeneficiaryFormatted,
  EmployeeDocumentFormatted,
  EmployeeFormatted,
  EmployeeWithBeneficiariesFormatted,
} from '@/api/employees/employees.types';
import type { PrestationTransactionFormatted } from '@/api/prestation-transactions/prestation-transactions.types';
import type { ActionLogFormatted } from '../../beneficiaries.types';
import {
  documentStatusToString,
  formatBeneficiary,
  formatEmployeeBeneficiaryDocument,
} from '../../utils/beneficiaries.utils';
import { getEmployeeStatusDisplay, RELATIONSHIP_OPTIONS } from '../employees.constants';

export function formatEmployee(
  employee: Employee & { employeeCommunications?: BaseEmployeeCommunication[] }
): EmployeeFormatted {
  return {
    ...employee,
    employeeCommunications: employee.employeeCommunications ?? [],
    statusFormatted: getEmployeeStatusDisplay(employee.status)?.label,
    seniority: employee.contractStartDate
      ? calculateSeniority(employee.contractStartDate)
      : undefined,
  };
}

export function formatEmployeeWithBeneficiaries(
  employee: EmployeeWithBeneficiaries
): EmployeeWithBeneficiariesFormatted {
  return {
    ...formatEmployee(employee),
    alerts: computeEmployeeAlerts(employee),
    beneficiary: formatBeneficiary(employee.beneficiary),
    employeeBeneficiaries: employee.employeeBeneficiaries.map(formatEmployeeBeneficiary),
    transactions: employee.transactions.map((transaction) =>
      formatPrestationTransaction(transaction, {
        employeeId: employee.id,
        beneficiaryIds: [
          employee.beneficiaryId,
          ...employee.employeeBeneficiaries.map((eb) => eb.beneficiaryId),
        ],
      })
    ),
    employeeDocuments: employee.employeeDocuments.map(formatEmployeeDocument),
    actionLogs: employee.actionLogs.map(formatActionLog),
  };
}

function formatActionLog(actionLog: BaseActionLog): ActionLogFormatted {
  return {
    ...actionLog,
    typeFormatted: actionLogTypeToString(actionLog.type),
  };
}

function actionLogTypeToString(type: ActionLogType): string {
  switch (type) {
    case ActionLogType.creation:
      return 'Création';
    case ActionLogType.modification:
      return 'Mise à jour';
    case ActionLogType.deletion:
      return 'Suppression';
  }
}

function calculateSeniority(contractStartDate: Date): string {
  return `${fullYearsSince(contractStartDate)} ans`;
}

export function formatEmployeeBeneficiary(
  employeeBeneficiary: BaseEmployeeBeneficiary
): EmployeeBeneficiaryFormatted {
  return {
    ...employeeBeneficiary,
    beneficiary: {
      ...formatBeneficiary(employeeBeneficiary.beneficiary),
      employeeBeneficiaryDocuments:
        employeeBeneficiary.beneficiary.employeeBeneficiaryDocuments.map(
          formatEmployeeBeneficiaryDocument
        ),
    },
    statusFormatted: employeeBeneficiaryStatusToString(employeeBeneficiary.status),
  };
}

export function employeeBeneficiaryStatusToString(status: EmployeeBeneficiaryStatus): string {
  return RELATIONSHIP_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

// `beneficiaryScope` narrows the displayed amount to the lines of the salarié whose fiche is open:
// one règlement can cover several dossiers (the salarié and their ayants-droit, or even two
// salariés), and showing its whole total on each fiche would inflate the amount.
export function formatPrestationTransaction(
  transaction: PrestationTransactionWithEmployee,
  beneficiaryScope?: { employeeId: number; beneficiaryIds: number[] }
): PrestationTransactionFormatted {
  const ownLines = beneficiaryScope
    ? transaction.prestationTransactions.filter((pt) => {
        const participation = pt.prestationParticipation;
        if (participation.externalEmployeeId === beneficiaryScope.employeeId) return true;
        return participation.participants.some(
          (participant) =>
            participant.externalBeneficiaryId !== null &&
            beneficiaryScope.beneficiaryIds.includes(participant.externalBeneficiaryId)
        );
      })
    : transaction.prestationTransactions;
  const totalAmount = sumDecimals(ownLines.map((pt) => pt.amount));
  // The mode de règlement is the treasury account itself: debited when the salarié pays, credited
  // when the CSE pays a créance out to them (negative allocations).
  const treasurySide = totalAmount.isNegative() ? 'credit' : 'debit';
  const bankMovement = transaction.transactionAccountMovements.find(
    (movement) => movement.transactionType === treasurySide
  );
  const methodFormatted = bankMovement
    ? bankMovement.financialAccount.name
      ? `${bankMovement.financialAccount.code} - ${bankMovement.financialAccount.name}`
      : bankMovement.financialAccount.code
    : '-';
  return {
    ...transaction,
    statusFormatted: transactionStatusToString(transaction.status),
    methodFormatted,
    totalAmount,
    depositStatus: deriveReglementDepositStatus(transaction),
  };
}

function transactionStatusToString(status: TransactionStatus): string {
  switch (status) {
    case TransactionStatus.awaitingValidation:
      return 'En attente de validation';
    case TransactionStatus.validated:
      return 'Validé';
  }
}

function formatEmployeeDocument(employeeDocument: EmployeeDocument): EmployeeDocumentFormatted {
  return {
    ...employeeDocument,
    statusFormatted: documentStatusToString(employeeDocument.status),
    typeFormatted: employeeDocumentTypeToString(employeeDocument.type),
  };
}

function employeeDocumentTypeToString(type: EmployeeDocumentType): string {
  switch (type) {
    case EmployeeDocumentType.identityCard:
      return "Carte d'identité";
    case EmployeeDocumentType.payslip:
      return 'Fiche de paie';
    case EmployeeDocumentType.rib:
      return 'RIB';
  }
}
